import { useMemo } from 'react';
import { StyleSheet, View, ScrollView } from 'react-native';
import { Button, Icon, Text, theme } from '@socrait/react';
import { marked } from 'marked';
import RenderHtml from 'react-native-render-html';

import { Loading } from '~/components/Loading';
import { AccordionBase, useAccordionState } from '~/components/Accordion';
import { ErrorBox } from '~/components/ErrorBox';
import { useRenderHtmlConfig } from '~/hooks/useRenderHtmlConfig';
import { useReviewClassSummary } from '~/services/review/useReviewClassSummaryQuery';

import { BaseActionItemProps } from './ActionItemTypes';

export const ClassSummaryActionItem = ({
  title,
  message,
  date,
  periodId,
}: BaseActionItemProps & { periodId: string }) => {
  const { isCollapsed, handleToggleCollapse, handleReviewButtonClick } = useAccordionState();
  const { data, status, error } = useReviewClassSummary({
    date,
    periodId: periodId,
    enabled: true,
  });

  // Parse markdown to HTML when data is available using useMemo
  const summaryHtml = useMemo(() => {
    if (data?.markdown) {
      return marked.parse(data.markdown) as string;
    }
    return '';
  }, [data?.markdown]);

  const accordionState = status === 'pending' ? 'loading' : 'loaded';

  const reviewButton = (
    <Button
      name="Review"
      onPress={handleReviewButtonClick}
      containerStyle={styles.reviewButton}
      fontStyle={styles.reviewButtonText}
      variant="primary"
    />
  );

  const getCopyText = () => {
    const text = data?.markdown || message || '';
    return {
      plain: text,
      html: summaryHtml || text,
    };
  };

  const { fonts, defaultTagStyles } = useRenderHtmlConfig();

  const tagsStyles = {
    ...defaultTagStyles,
    h1: { fontFamily: theme.fonts.bold, fontWeight: 'bold', fontSize: 18, marginBottom: 8 },
    h2: { fontFamily: theme.fonts.bold, fontWeight: 'bold', fontSize: 16, marginTop: 12, marginBottom: 4 },
    h3: { fontFamily: theme.fonts.bold, fontWeight: 'bold', fontSize: 15, marginTop: 8, marginBottom: 4 },
    p: { fontFamily: theme.fonts.regular, lineHeight: 20, marginBottom: 8 },
    li: { fontFamily: theme.fonts.regular, lineHeight: 20, marginLeft: 16 },
    ul: { marginLeft: 0 },
    ol: { marginLeft: 0 },
    strong: { fontFamily: theme.fonts.bold, fontWeight: 'bold' },
    em: { fontFamily: theme.fonts.regular, fontStyle: 'italic' },
  };

  const getContent = () => {
    if (error) {
      return (
        <ErrorBox text="Could not load class summary. Please try reopening this action item or reloading the window." />
      );
    }

    if (status === 'pending') {
      return (
        <View style={styles.loadingContainer}>
          <Loading backgroundColor={theme.colors.transparent} />
        </View>
      );
    }

    if (summaryHtml) {
      return (
        <ScrollView style={styles.summaryContainer} showsVerticalScrollIndicator={true}>
          <RenderHtml source={{ html: summaryHtml }} tagsStyles={tagsStyles} systemFonts={fonts} />
        </ScrollView>
      );
    }

    if (data?.markdown) {
      return (
        <ScrollView style={styles.summaryContainer} showsVerticalScrollIndicator={true}>
          <Text>{data.markdown}</Text>
        </ScrollView>
      );
    }

    if (message) {
      return (
        <ScrollView style={styles.summaryContainer} showsVerticalScrollIndicator={true}>
          <Text>{message}</Text>
        </ScrollView>
      );
    }

    return <Text style={styles.noDataText}>No data available</Text>;
  };

  return (
    <AccordionBase
      title={title}
      icon={<Icon type="square" isSolid={true} />}
      rightControls={reviewButton}
      content={getContent()}
      copyText={getCopyText()}
      isCollapsed={isCollapsed}
      onToggleCollapse={handleToggleCollapse}
      state={accordionState}
      autoExpand={false}
      onReviewButtonClick={handleReviewButtonClick}
      noContentBorder={!!error}
    />
  );
};

const styles = StyleSheet.create({
  summaryContainer: {
    padding: theme.spacing.medium,
    maxHeight: 360,
    flex: 1,
  },
  loadingContainer: {
    height: 400,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  noDataText: {
    color: theme.colors.darkGray,
    textAlign: 'center',
    padding: theme.spacing.large,
  },
  reviewButton: {
    borderRadius: theme.borderRadius.xlarge,
  },
  reviewButtonText: {
    fontSize: theme.fontSizes.small,
    fontWeight: theme.fontWeights.bold,
  },
});
